"use client";

import type { ExternalClientApp } from "@mlx/contracts";
import { BlocksIcon, BracesIcon, MonitorIcon, TerminalIcon } from "lucide-react";
import { useState } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { TwinMark } from "./twin-mark";

export type ProvenanceActor = {
  label: string;
  imageUrl: string | null;
  initials: string;
  icon?: "twin";
  identityId?: string;
};
export type ProvenanceInterface = "web" | "cli" | "api" | "mcp" | "ios";

export function ProvenanceAvatarStack(props: {
  actor?: ProvenanceActor | null;
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  usedTwin?: boolean;
  executedByTwin?: boolean;
  action: "started" | "created" | "updated" | "posted" | "retrieved";
  size?: "sm" | "md";
  layout?: "stack" | "overlay";
  className?: string;
}) {
  const sourceVisible = Boolean(props.clientApp || props.interfaceType);
  const twinVisible = Boolean(props.usedTwin || props.executedByTwin);
  if (!props.actor && !sourceVisible && !twinVisible) return null;
  const description = provenanceDescription(props);
  const sizeClass = props.size === "md" ? "size-8" : "size-5";
  const iconClass = props.size === "md" ? "size-4" : "size-3";
  const overlay = props.layout === "overlay" && Boolean(props.actor) && (sourceVisible || twinVisible);
  return (
    <TooltipProvider delayDuration={180}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={description}
            className={cn(
              "inline-flex items-center",
              overlay ? "relative" : "-space-x-1.5",
              props.className,
            )}
            tabIndex={0}
          >
            {props.actor ? <ActorMark actor={props.actor} className={sizeClass} /> : null}
            {overlay ? (
              <span
                data-slot="provenance-badges"
                className="absolute right-0 -top-1 flex flex-col -space-y-1"
                aria-hidden
              >
                {twinVisible ? <TwinProvenanceMark className="size-4 ring-1" iconClassName="size-2.5" /> : null}
                {sourceVisible ? (
                  <SourceMark
                    clientApp={props.clientApp}
                    interfaceType={props.interfaceType}
                    className="size-4 ring-1"
                    iconClassName="size-2.5"
                  />
                ) : null}
              </span>
            ) : (
              <>
                {sourceVisible ? (
                  <SourceMark
                    clientApp={props.clientApp}
                    interfaceType={props.interfaceType}
                    className={sizeClass}
                    iconClassName={iconClass}
                  />
                ) : null}
                {twinVisible ? <TwinProvenanceMark className={sizeClass} iconClassName={iconClass} /> : null}
              </>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const markClass = "relative flex shrink-0 items-center justify-center rounded-full border border-border/70 bg-background ring-2 ring-background";

function SourceMark(props: {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  className: string;
  iconClassName: string;
}) {
  return (
    <span className={cn(markClass, "overflow-hidden text-foreground", props.className)} aria-hidden>
      <SourceIcon
        clientApp={props.clientApp}
        interfaceType={props.interfaceType}
        className={sourceIconClassName(props.clientApp, props.iconClassName)}
      />
    </span>
  );
}

function TwinProvenanceMark(props: { className: string; iconClassName: string }) {
  return (
    <span className={cn(markClass, props.className)} aria-hidden>
      <TwinMark className={props.iconClassName} />
    </span>
  );
}

function ActorMark({ actor, className }: { actor: ProvenanceActor; className: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(actor.imageUrl && actor.imageUrl !== failedUrl);
  return (
    <span className={cn(markClass, "overflow-hidden bg-muted text-[9px] font-semibold text-muted-foreground", className)} aria-hidden>
      {actor.icon === "twin" ? (
        <TwinMark className="size-4" />
      ) : showImage ? (
        <img src={actor.imageUrl ?? undefined} alt="" className="size-full object-cover" referrerPolicy="no-referrer" onError={() => setFailedUrl(actor.imageUrl)} />
      ) : actor.initials}
    </span>
  );
}

function sourceIconClassName(
  clientApp: ExternalClientApp | null | undefined,
  className: string,
) {
  const monochromeBrand =
    clientApp === "claude-code" || clientApp === "codex" || clientApp === "gemini";
  return cn(className, monochromeBrand && "dark:invert");
}

function SourceIcon(props: { clientApp?: ExternalClientApp | null; interfaceType?: ProvenanceInterface | null; className: string }) {
  if (props.clientApp) {
    const asset = props.clientApp === "claude-code" ? "claude" : props.clientApp;
    return <img alt="" src={`/client-icons/${asset}.svg`} className={props.className} />;
  }
  if (props.interfaceType === "cli") return <TerminalIcon className={props.className} />;
  if (props.interfaceType === "mcp") return <BlocksIcon className={props.className} />;
  if (props.interfaceType === "api") return <BracesIcon className={props.className} />;
  return <MonitorIcon className={props.className} />;
}

function provenanceDescription(props: { actor?: ProvenanceActor | null; clientApp?: ExternalClientApp | null; interfaceType?: ProvenanceInterface | null; usedTwin?: boolean; executedByTwin?: boolean; action: string }) {
  const actor = props.actor
    ? props.actor.identityId
      ? `${props.actor.label} (${props.actor.identityId})`
      : props.actor.label
    : "MLX";
  const app = clientAppLabel(props.clientApp);
  const via = props.interfaceType ? interfaceLabel(props.interfaceType) : null;
  const source = app ? ` using ${app}${via ? ` via ${via}` : ""}` : via ? ` via ${via}` : "";
  const twin = props.executedByTwin
    ? ". The Twin executed the action"
    : props.usedTwin ? " with governed data from the Twin" : "";
  return `${actor} ${props.action} this${source}${twin}.`;
}

export function clientAppLabel(value?: ExternalClientApp | null) {
  if (value === "claude-code") return "Claude Code";
  if (value === "codex") return "Codex";
  if (value === "gemini") return "Gemini";
  if (value === "chatgpt") return "ChatGPT";
  return null;
}

export function interfaceLabel(value: ProvenanceInterface) {
  if (value === "cli") return "CLI";
  if (value === "mcp") return "MCP";
  if (value === "api") return "API";
  if (value === "ios") return "iOS";
  return "web app";
}

export function sessionClientProvenance(metadata: Record<string, unknown> | undefined) {
  const client = metadata?.client;
  if (!client || typeof client !== "object" || Array.isArray(client)) return {};
  const record = client as Record<string, unknown>;
  const app = record.app;
  const source = record.source;
  return {
    ...(app === "claude-code" || app === "codex" || app === "gemini" || app === "chatgpt" ? { clientApp: app as ExternalClientApp } : {}),
    ...(source === "web" || source === "cli" || source === "api" || source === "mcp" || source === "ios" ? { interfaceType: source as ProvenanceInterface } : {}),
  };
}

export function channelClientProvenance(metadata: Record<string, unknown> | undefined) {
  const value = metadata?.mlxProvenance;
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const app = record.clientApp;
  const source = record.interfaceType;
  return {
    ...(app === "claude-code" || app === "codex" || app === "gemini" || app === "chatgpt" ? { clientApp: app as ExternalClientApp } : {}),
    ...(source === "web" ? { interfaceType: "web" as const } : {}),
  };
}
